import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import * as teacherService from '../services/teacherService';
import type {
  TeacherDailyReport
} from '../services/teacherService';

// Teacher-specific interfaces
export interface Teacher {
  id: number;
  full_name: string;
  email: string;
  phone?: string;
  employee_id: string;
  department: string;
  hire_date: string;
  qualifications: string[];
  specializations: string[];
  class_assignments: TeacherClass[];
  status: 'active' | 'inactive' | 'on_leave';
}

export interface TeacherClass {
  id: number;
  name: string;
  grade_level: string;
  student_count: number;
  room_number: string;
  schedule: {
    start_time: string;
    end_time: string;
    days: string[];
  };
  students: TeacherStudent[];
}

export interface TeacherStudent {
  id: string; // Changed to string for UUID
  first_name: string;
  last_name: string;
  date_of_birth: string;
  age: number;
  photo_url?: string;
  attendance_status: 'present' | 'absent' | 'late' | 'early_pickup';
  arrival_time?: string;
  pickup_time?: string;
  medical_alerts?: string[];
  parent_contacts: {
    name: string;
    relationship: string;
    phone: string;
    email: string;
    is_primary: boolean;
  }[];
  recent_activities: {
    id: number;
    type: string;
    timestamp: string;
    location: string;
    notes?: string;
  }[];
}

export interface DailyReport {
  id: string; // Changed to string for UUID
  student_id: string; // Changed to string for UUID
  teacher_id: string; // Changed to string for UUID
  date: string;
  arrival_time?: string;
  departure_time?: string;
  mood: 'happy' | 'sad' | 'neutral' | 'excited' | 'tired' | 'upset';
  meals: {
    breakfast?: { eaten: boolean; amount: 'none' | 'little' | 'some' | 'all'; notes?: string };
    lunch?: { eaten: boolean; amount: 'none' | 'little' | 'some' | 'all'; notes?: string };
    snack?: { eaten: boolean; amount: 'none' | 'little' | 'some' | 'all'; notes?: string };
  };
  nap: {
    start_time?: string;
    end_time?: string;
    duration?: number;
    quality: 'excellent' | 'good' | 'fair' | 'poor' | 'no_nap';
    notes?: string;
  };
  activities: {
    type: string;
    duration: number;
    participation: 'excellent' | 'good' | 'fair' | 'poor';
    notes?: string;
  }[];
  behavior: {
    overall: 'excellent' | 'good' | 'fair' | 'needs_improvement';
    highlights?: string[];
    concerns?: string[];
    notes?: string;
  };
  milestones?: string[];
  teacher_notes: string;
  parent_communication?: {
    message: string;
    requires_response: boolean;
  };
  photos: string[];
  status: 'draft' | 'completed' | 'sent';
}

export interface TeacherMessage {
  id: number;
  parent_id: number;
  parent_name: string;
  student_id: number;
  student_name: string;
  subject: string;
  message: string;
  timestamp: string;
  status: 'unread' | 'read' | 'replied';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  thread_id?: number;
}

export interface ActivityVerification {
  id: number;
  student_id: number;
  student_name: string;
  activity_type: string;
  timestamp: string;
  location: string;
  confidence: number;
  image_url?: string;
  video_url?: string;
  ai_description: string;
  verification_status: 'pending' | 'verified' | 'rejected' | 'needs_review';
  teacher_notes?: string;
  tags?: string[];
}

// Teacher hooks
export const useTeacherProfile = () => {
  return useQuery({
    queryKey: ['teacher', 'profile'],
    queryFn: async () => {
      const response = await teacherService.getTeacherProfile();
      return response;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useTeacherClasses = () => {
  return useQuery({
    queryKey: ['teacher', 'classes'],
    queryFn: async () => {
      const response = await teacherService.getTeacherClasses();
      // The service now returns StandardListResponse<ClassRoom>
      return response.items;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useClassAttendance = (classId?: string, date?: Date) => {
  return useQuery({
    queryKey: ['teacher', 'attendance', classId, date?.toISOString()],
    queryFn: async () => {
      if (!classId || !date) {
        return { attendance: [] };
      }

      const response = await teacherService.getClassAttendance(classId, date);
      return response;
    },
    enabled: !!classId && !!date,
  });
};

export const useClassStudents = (classId?: string) => {
  return useQuery({
    queryKey: ['teacher', 'class', classId, 'students'],
    queryFn: async () => {
      if (!classId) return [];
      const response = await teacherService.getTeacherClassStudents(classId);
      // The service now returns StandardListResponse<Student>
      return response.items;
    },
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTeacherMessages = (filters?: any) => {
  return useQuery({
    queryKey: ['teacher', 'messages', filters],
    queryFn: async () => {
      const response = await teacherService.getTeacherMessages(filters);
      // The service now returns { messages: any[], pagination?: any }
      return response.messages || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useDailyReports = (date?: string, classId?: string) => {
  return useQuery({
    queryKey: ['teacher', 'daily_reports', date, classId],
    queryFn: async () => {
      const response = await teacherService.getTeacherDailyReports({ date, class_id: classId });
      // The service now returns StandardListResponse<TeacherDailyReport>
      return response.items;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useActivityVerifications = (params?: { page?: number; per_page?: number }) => {
  return useQuery({
    queryKey: ['teacher', 'verifications', params],
    queryFn: async () => {
      const response = await teacherService.getActivitiesToVerify(params);
      // The service now returns StandardListResponse<TeacherActivityVerification>
      return response.items;
    },
    staleTime: 2 * 60 * 1000,
  });
};

// Mutation hooks
export const useCreateDailyReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (report: {
      student_id: string;
      report_date: string;
      attendance_status: 'present' | 'absent' | 'late' | 'excused';
      arrival_time?: string;
      departure_time?: string;
      meals?: {
        breakfast?: string;
        lunch?: string;
        snack?: string;
      };
      nap_time?: {
        start?: string;
        end?: string;
      };
      activities?: Array<{
        time: string;
        activity: string;
        notes?: string;
      }>;
      mood?: 'happy' | 'sad' | 'neutral' | 'energetic' | 'tired';
      health_notes?: string;
      teacher_notes?: string;
    }) => {
      const response = await teacherService.createDailyReport(report);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'daily_reports'] });
      notifications.show({
        title: 'Report Created',
        message: 'Daily report has been created successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to create daily report',
        color: 'red',
      });
    },
  });
};

export const useUpdateDailyReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, report }: { id: string; report: Partial<TeacherDailyReport> }) => {
      const response = await teacherService.updateDailyReport(id, report);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'daily_reports'] });
      notifications.show({
        title: 'Report Updated',
        message: 'Daily report has been updated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update daily report',
        color: 'red',
      });
    },
  });
};

export const useVerifyActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, verified, notes }: { id: string; verified: boolean; notes?: string }) => {
      await teacherService.verifyActivity(id, { verified, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'verifications'] });
      notifications.show({
        title: 'Activity Verified',
        message: 'Activity verification has been updated',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to verify activity',
        color: 'red',
      });
    },
  });
};

export const useSendParentMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ parentId, subject, message }: { parentId: string; subject: string; message: string }) => {
      const response = await teacherService.sendParentMessage({ parentId, subject, message });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'messages'] });
      notifications.show({
        title: 'Message Sent',
        message: 'Your message has been sent to the parent',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to send message',
        color: 'red',
      });
    },
  });
};

export const useMarkAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (attendance: {
      class_id: string;
      date: string;
      attendance: Array<{
        student_id: string;
        status: string;
      }>;
    }) => {
      await teacherService.markAttendance(attendance);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'class'] });
      queryClient.invalidateQueries({ queryKey: ['teacher', 'attendance'] });
      notifications.show({
        title: 'Attendance Updated',
        message: 'Class attendance has been recorded',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update attendance',
        color: 'red',
      });
    },
  });
};

// Legacy hook for backward compatibility
export const useUpdateStudentAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ studentId, status, date, notes }: { studentId: string; status: string; date: string; notes?: string }) => {
      const response = await teacherService.updateAttendance(studentId, { status, date, notes });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'class'] });
      queryClient.invalidateQueries({ queryKey: ['teacher', 'attendance'] });
      notifications.show({
        title: 'Attendance Updated',
        message: 'Student attendance has been recorded',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update attendance',
        color: 'red',
      });
    },
  });
};

// Enhanced teacher hooks using real API endpoints

// Get teacher dashboard data using user endpoint
export const useTeacherDashboardFromUser = (teacherId?: string) => {
  return useQuery({
    queryKey: ['teacher', 'dashboard-user', teacherId],
    queryFn: () => teacherService.getTeacherDashboardFromUser(teacherId),
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider stale after 30 seconds
  });
};

// Get teacher's students using students endpoint
export const useTeacherStudentsFromAPI = (
  teacherId?: string,
  params?: { class_id?: string; grade_level?: string; limit?: number }
) => {
  return useQuery({
    queryKey: ['teacher', 'students-api', teacherId, params],
    queryFn: () => teacherService.getTeacherStudentsFromAPI(teacherId, params),
    enabled: !!teacherId,
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Get activities for verification using detections endpoint
export const useActivitiesForVerification = (
  params?: {
    verified?: boolean;
    date?: string;
    student_id?: string;
    limit?: number;
    confidence_threshold?: number;
  }
) => {
  return useQuery({
    queryKey: ['teacher', 'activities-verification', params],
    queryFn: () => teacherService.getActivitiesForVerification(params),
    refetchInterval: 30000, // Refresh every 30 seconds for real-time verification
  });
};

// Verify activity detection
export const useVerifyActivityDetection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ detectionId, verified, notes }: {
      detectionId: string;
      verified: boolean;
      notes?: string;
    }) => teacherService.verifyActivityDetection(detectionId, verified, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'activities-verification'] });
      notifications.show({
        title: 'Activity Verified',
        message: 'Activity verification has been updated',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to verify activity',
        color: 'red',
      });
    },
  });
};

// Get teacher's class roster using students endpoint
export const useTeacherClassRoster = (
  classId?: string,
  params?: { grade_level?: string; active_only?: boolean }
) => {
  return useQuery({
    queryKey: ['teacher', 'class-roster', classId, params],
    queryFn: () => teacherService.getTeacherClassRoster(classId, params),
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Generate attendance report using student data
export const useGenerateAttendanceReport = () => {
  return useMutation({
    mutationFn: (params?: {
      date?: string;
      class_id?: string;
      grade_level?: string;
      start_date?: string;
      end_date?: string;
    }) => teacherService.generateAttendanceReport(params),
    onSuccess: () => {
      notifications.show({
        title: 'Report Generated',
        message: 'Attendance report has been generated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate attendance report',
        color: 'red',
      });
    },
  });
};

// Get teacher messages using notifications endpoint (enhanced version)
export const useTeacherMessagesFromAPI = (
  params?: { unread_only?: boolean; limit?: number }
) => {
  return useQuery({
    queryKey: ['teacher', 'messages-api', params],
    queryFn: () => teacherService.getTeacherMessagesFromAPI(params),
    refetchInterval: 60000, // Refresh every minute for messages
  });
};

// Get student progress using detection data
export const useStudentProgress = (
  studentId: string | undefined,
  params?: { start_date?: string; end_date?: string; activity_types?: string[] }
) => {
  return useQuery({
    queryKey: ['teacher', 'student-progress', studentId, params],
    queryFn: () => teacherService.getStudentProgress(studentId!, params),
    enabled: !!studentId,
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Get unverified activities count for badge
export const useUnverifiedActivitiesCount = () => {
  return useQuery({
    queryKey: ['teacher', 'unverified-count'],
    queryFn: async () => {
      const activities = await teacherService.getActivitiesForVerification({
        verified: false,
        limit: 100,
      });
      return activities.length;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

// Get today's class summary
export const useTodayClassSummary = (teacherId?: string) => {
  return useQuery({
    queryKey: ['teacher', 'today-summary', teacherId],
    queryFn: async () => {
      const dashboard = await teacherService.getTeacherDashboardFromUser(teacherId);
      const today = new Date().toISOString().split('T')[0];

      const attendanceReport = await teacherService.generateAttendanceReport({
        date: today,
      });

      return {
        date: today,
        classes: dashboard.quick_stats.classes,
        total_students: dashboard.quick_stats.students,
        present_students: attendanceReport.present_count,
        absent_students: attendanceReport.absent_count,
        attendance_rate: attendanceReport.attendance_rate,
        activities_today: dashboard.recent_activity.activities_today,
        pending_verifications: dashboard.quick_stats.pending_tasks,
        alerts_today: dashboard.quick_stats.alerts_today,
      };
    },
    enabled: !!teacherId,
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Enhanced communication and daily report hooks

// Generate teacher daily report
export const useGenerateTeacherDailyReport = () => {
  return useMutation({
    mutationFn: ({ teacherId, date }: { teacherId?: string; date?: string }) =>
      teacherService.generateTeacherDailyReport(teacherId, date),
    onSuccess: () => {
      notifications.show({
        title: 'Daily Report Generated',
        message: 'Your daily report has been generated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate daily report',
        color: 'red',
      });
    },
  });
};

// Send message to parent
export const useSendMessageToParent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      parent_id?: string;
      student_id?: string;
      subject: string;
      message: string;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
    }) => teacherService.sendMessageToParent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'messages'] });
      notifications.show({
        title: 'Message Sent',
        message: 'Your message has been sent successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to send message',
        color: 'red',
      });
    },
  });
};

// Get teacher communication history
export const useTeacherCommunicationHistory = (
  teacherId?: string,
  params?: { limit?: number; type?: string }
) => {
  return useQuery({
    queryKey: ['teacher', 'communication-history', teacherId, params],
    queryFn: () => teacherService.getTeacherCommunicationHistory(teacherId, params),
    enabled: !!teacherId,
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Bulk verify activities
export const useBulkVerifyActivities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ activityIds, verified, notes }: {
      activityIds: string[];
      verified: boolean;
      notes?: string;
    }) => teacherService.bulkVerifyActivities(activityIds, verified, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'activities-verification'] });
      queryClient.invalidateQueries({ queryKey: ['teacher', 'unverified-count'] });
      notifications.show({
        title: 'Activities Updated',
        message: `${variables.activityIds.length} activities have been ${variables.verified ? 'verified' : 'rejected'}`,
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update activities',
        color: 'red',
      });
    },
  });
};

// Get teacher daily report
export const useTeacherDailyReport = (
  teacherId?: string,
  date?: string
) => {
  return useQuery({
    queryKey: ['teacher', 'daily-report', teacherId, date],
    queryFn: () => teacherService.generateTeacherDailyReport(teacherId, date),
    enabled: !!teacherId,
    refetchInterval: 3600000, // Refresh every hour
  });
};

// Import available teachers hook from parent service
export const useAvailableTeachers = () => {
  return useQuery({
    queryKey: ['teacher', 'available-teachers'],
    queryFn: async () => {
      // Use the parent service function since it's the same endpoint
      const { getAvailableTeachers } = await import('../services/parentService');
      return getAvailableTeachers();
    },
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};
